import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  Validators,
  FormControl,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { CustomerDetailsData } from '../ahoy-customer.service';

interface AddressTypeButton {
  id: number;
  name: string;
  icon: string;
}

/**
 * The PascalCase-keyed request body built up by CustomerDetailsComponent.getValues()
 * and enriched here with the address-type-specific fields. Exported so
 * customer-details.component.ts (which owns/returns this body) can share the
 * same shape instead of redeclaring it.
 */
export interface CustomerAddressFormBody {
  CustomerAddress?: string | { title?: string };
  DeliveryDateTime?: Date | number;
  CustomerAddressTypeId?: number;
  Building?: string;
  Floor?: string;
  Unit?: string;
  Area?: string;
  CustomerAddressNote?: string;
  tagLocationName?: string;
  [key: string]: unknown;
}

@Component({
  selector: 'app-address-details-tabs',
  templateUrl: './address-details-tabs.component.html',
  styleUrls: ['./address-details-tabs.component.scss'],
  standalone: false,
})
export class AddressDetailsTabsComponent implements OnInit {
  addressFormGroup: FormGroup;

  addressSelectedIndex = 2;
  addressTypesButtons: Array<AddressTypeButton> = [
    { id: 2, name: 'Villa', icon: 'home_work' },
    { id: 1, name: 'Apartment', icon: 'apartment' },
    { id: 3, name: 'Office/Hotel', icon: 'domain' },
  ];

  constructor(
    private _formBuilder: FormBuilder,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService,
  ) {
    this.addressFormGroup = this._formBuilder.group({
      buildingNameBuilding: [
        '',
        [Validators.required, Validators.maxLength(40)],
      ],
      areaBuilding: ['', [Validators.required, Validators.maxLength(120)]],
      floorBuilding: ['', [Validators.max(99), Validators.pattern(/^\d+$/)]],
      unitBuilding: ['', [Validators.maxLength(10)]],
      noteBuilding: ['', [Validators.maxLength(255)]],
      addressBtnsFormGroup: this._formBuilder.group({
        addressBtns: new FormControl(''),
      }),
    });
  }

  ngOnInit(): void {
    this.cdr.detectChanges();
  }

  /**
   * determine whether the data is valid or not
   * @param touchForm whether to mark the form as touched
   */
  isValid(touchForm = true) {
    if (touchForm) {
      this.addressFormGroup.markAllAsTouched();
    }
    return this.addressFormGroup.valid;
  }

  bindAddressInformation(data: CustomerDetailsData) {
    this.bindAddressType(data);
    if (!data.area) {
      data.area = data.customerAddress;
    }
    this.addressFormGroup.get('areaBuilding')!.setValue(data.area);
    this.addressFormGroup.get('buildingNameBuilding')!.setValue(data.building);
    this.addressFormGroup.get('floorBuilding')!.setValue(data.floor);
    this.addressFormGroup.get('unitBuilding')!.setValue(data.unit);
    this.addressFormGroup
      .get('noteBuilding')!
      .setValue(data.customerAddressNote);
  }

  getBuildingNameTitle() {
    const selectedAddressType = this.addressTypesButtons.find(
      (item) => item.id === this.addressSelectedIndex,
    );
    return this.translateService.instant(
      `${selectedAddressType.name} Name or Number`,
    );
  }

  /**
   * set the buttons to the correct address type
   * @param res customer data that contains customerAddressTypeId
   */
  bindAddressType(res: CustomerDetailsData) {
    let addressType = res.customerAddressTypeId;
    // handle the case when the type is 0, by default make it 1
    if (!addressType) {
      addressType = 2;
    }
    // Bind the button to the corresponding customerAddressTypeId
    this.addressSelectedIndex = addressType;
    this.onBuildingTypeChange(addressType);
  }

  /**
   * trigger when the address button changed.
   * the method is used to change the validation on the form fields
   * make the unselected button fields unrequired and the selected one required
   * @param typeIndex address type id
   */
  onBuildingTypeChange(typeIndex: number) {
    const buildingControl = this.addressFormGroup.get('buildingNameBuilding');
    const areaControl = this.addressFormGroup.get('areaBuilding');
    const floorControl = this.addressFormGroup.get('floorBuilding');
    const unitControl = this.addressFormGroup.get('unitBuilding');

    buildingControl!.setValidators([Validators.required]);
    areaControl!.setValidators([Validators.required]);

    if (typeIndex === 1) {
      floorControl!.setValidators([Validators.required]);
      unitControl!.setValidators([]);
    } else {
      floorControl!.setValidators([]);
      unitControl!.setValidators([]);
    }

    this.addressSelectedIndex = typeIndex;
    buildingControl!.updateValueAndValidity();
    areaControl!.updateValueAndValidity();
    floorControl!.updateValueAndValidity();
    unitControl!.updateValueAndValidity();
  }

  setAreaValue(address: string) {
    this.addressFormGroup.get('areaBuilding')!.setValue(address);
  }

  updateBodyValues(body: CustomerAddressFormBody) {
    body.CustomerAddressTypeId = this.addressSelectedIndex;

    body.Building = this.addressFormGroup.get('buildingNameBuilding')!.value;
    body.Floor = this.addressFormGroup.get('floorBuilding')!.value;
    body.Unit = this.addressFormGroup.get('unitBuilding')!.value;
    body.Area = this.addressFormGroup.get('areaBuilding')!.value;
    body.CustomerAddressNote = this.addressFormGroup.get('noteBuilding')!.value;
  }
}
